import axios from 'axios';
import { createApiUrl, getToken } from '../../util';
import api from 'api/axiosInstance';
import { ShadingStatusSnap } from 'api/editDsm';

const authHeader = () => ({ Authorization: `Bearer ${getToken('Authorization')}` });

/** Async-create lifecycle. Same string values across the 202 body, status poll and SSE. */
export type DesignCreateStatus = 'PENDING' | 'PROCESSING' | 'READY' | 'FAILED';

/** 202 Accepted body from POST /designs (and /designs/roof/create) — a tracking id, no design. */
export type CreateDesignAccepted = {
	designUUID: string;
	externalID: string;
	billAmount?: number;
	status: DesignCreateStatus;
};

/**
 * GET /designs/create-status/:uuid — the ONE poll for a queued build.
 * `retryable` disambiguates FAILED: `true` = a retry is queued (keep watching), `false` = terminal.
 * `source`/`mode` are non-null only once `status === 'READY'`.
 *
 * `shading` is the build's own first shading run, in the same shape as
 * GET /edit-dsm/status/:uuid — so one request covers both phases and the same reducer
 * reconciles it. It is null until the build seeds the run (most of the build), and stays
 * null forever for a mode that has no shading; absent is "nothing yet", never an error.
 */
export type CreateStatusSnap = {
	designUUID: string;
	status: DesignCreateStatus;
	errorCode: string | null;
	retryable?: boolean;
	attempts?: number;
	source?: string | null;
	mode?: DesignModes | null;
	metadata?: { waiting: number; active: number } | null;
	shading?: ShadingStatusSnap | null;
};

export type CreateDesignReqPayload= {
	externalID: string;
	billAmount?: number;
	panelKey?: string;
	gutterSetback?: number;
	nonGutterSetback?:number; 
	obstructionSetback?: number;
	designType?: DesignType;
	// flag to run shading analysis inline with ceate design
	// false: for autosave mode(tool render mode === background)
	deferShadingAnalysis?: boolean;
	setbacks?:{
		eaves?: number;
		gutters?: number;
		rakes?: number;
		ridges?: number;
		valleys?: number;
		hips?: number;
		obstructions?: number;
	} 
}

async function createNewDesign(coord: string | undefined, payload: CreateDesignReqPayload ) {
	const myToken = getToken('Authorization');
	const config = {
		headers: {
			'Authorization': `Bearer ${myToken}`
		}
	};

	if (!coord) return;
	const [lat, lng] = coord.split(',').map(eachVal => Number(eachVal));

	const transfomedPaylaod = {...removeUndefinedKeys(payload)};
	if('setbacks' in transfomedPaylaod){
		delete transfomedPaylaod['gutterSetback'];
		delete transfomedPaylaod['nonGutterSetback'];
		delete transfomedPaylaod['obstructionSetback'];
	}

	const requestBody: CreateDesignReqPayload & { latLng: Coordinate } = {
		latLng: {
			latitude: lat,
			longitude: lng
		},
		...transfomedPaylaod
		
	};

	// 202 Accepted — the build is queued on a background worker. Track via SSE/poll, then refetch.
	const { data } = await api.post(createApiUrl('/designs'), requestBody, config);

	return data as CreateDesignAccepted;
}

async function createNewDesignRoof(coord: string | undefined, payload: CreateDesignReqPayload ) {
	const myToken = getToken('Authorization');
	const config = {
		headers: {
			'Authorization': `Bearer ${myToken}`
		}
	};

	if (!coord) return;
	const [lat, lng] = coord.split(',').map(eachVal => Number(eachVal));

	const requestBody: CreateDesignReqPayload & { latLng: Coordinate } = {
		latLng: {
			latitude: lat,
			longitude: lng
		},
		...removeUndefinedKeys(payload)
		
	};

	const { data } = await api.post(createApiUrl('/designs/roof/create'), requestBody, config);

	return data as CreateDesignAccepted;
}

/** Poll fallback + initial state read for a queued build. MANDATORY (proxy-buffer / multi-instance safe). */
async function getCreateStatus(uuid: string): Promise<CreateStatusSnap> {
	const { data } = await api.get(createApiUrl(`/designs/create-status/${uuid}`), { headers: authHeader() });
	return data;
}

type productionDataType = {
	systemSize: number,
	energy: number,
	offset: number,
	totalPanels: number
}

type DesignSaveObj = {
	selectedPanels: string[],
	productionData: productionDataType,
	billAmount: number,
	annualEnergy?: number,
	panelKey: string,
	source: string,
}

async function updateDesing(designSaveObj: DesignSaveObj, uuid: string): Promise<{ bbox: HouseBoundingBox }> {

	const myToken = getToken('Authorization');
	const config = {
		headers: {
			'Authorization': `Bearer ${myToken}`
		}
	};

	const { data } = await api.patch(createApiUrl(`/designs/${uuid}`), designSaveObj, config);
	return data;
}

async function getUnselectedPanels(uuid: string) {
	const myToken = getToken('Authorization');
	const config = {
		headers: {
			'Authorization': `Bearer ${myToken}`
		}
	};

	const { data } = await api.get(createApiUrl(`/designs/${uuid}`), config);
	return data;
}

function removeUndefinedKeys<T extends Record<string, any>>(payload: T): T {
	const cleanedEntries = Object.entries(payload)
		.map(([key, value]) => {
			// Remove undefined values
			if (value === undefined) return null;

			// Recursively clean objects (but not arrays)
			if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
				const cleanedObject = removeUndefinedKeys(value);

				// If object is empty after cleaning, remove it
				if (Object.keys(cleanedObject).length === 0) {
					return null;
				}

				return [key, cleanedObject];
			}

			return [key, value];
		})
		.filter(Boolean) as [string, any][];

	return Object.fromEntries(cleanedEntries) as T;
}

export {
	createNewDesign,
	updateDesing,
	getUnselectedPanels,
	createNewDesignRoof,
	getCreateStatus
};
